import { forwardRef } from "react";
import { Typography } from "./Typography";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** Shown below the input; uses design system helper variant */
  helperText?: string;
  /** Shown in a tooltip on the (i) icon next to the label; describes what the field is for */
  tooltip?: string;
}

const inputBase =
  "block w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:focus:border-primary-400 dark:focus:ring-primary-500/30 outline-none transition-all min-h-[44px] disabled:bg-slate-50 dark:disabled:bg-slate-800/50 disabled:text-slate-500 dark:disabled:text-slate-500 [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert";
const inputError =
  "border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500/20 dark:focus:border-red-500 dark:focus:ring-red-500/30";

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, tooltip, className = "", ...props }, ref) => {
    return (
      <div className="space-y-1.5">
        {(label || tooltip) && (
          <div className="flex items-center gap-1.5">
            {label && (
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}
              </label>
            )}
            {tooltip && (
              <span
                className="inline-flex shrink-0 rounded-full p-0.5 text-slate-400 dark:text-slate-500 hover:text-primary-500 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-800 cursor-help"
                title={tooltip}
                tabIndex={0}
                role="img"
                aria-label={tooltip}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                </svg>
              </span>
            )}
          </div>
        )}
        <input
          ref={ref}
          className={`${inputBase} ${error ? inputError : ""} ${props.type === "date" ? "dark:[color-scheme:dark]" : ""} ${className}`}
          {...props}
        />
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {helperText && !error && (
          <Typography variant="helper">{helperText}</Typography>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
